import { useEffect } from "react";
import { trpc } from "@/lib/trpc";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function bytesToGb(bytes: number) {
  return (bytes / 1024 ** 3).toFixed(2);
}

function formatUptime(seconds: number) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

function formatInterval(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${ms / 1000}s`;
  return `${ms / 60000}min`;
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR");
}

export default function Processamento() {
  const utils = trpc.useContext();

  const { data: snap, isLoading } = trpc.processamento.snapshot.useQuery(undefined, {
    refetchInterval: 5000,
    staleTime: 0,
  });

  const { data: historico } = trpc.processamento.historico.useQuery(undefined, {
    refetchInterval: 60000,
    staleTime: 0,
  });

  useEffect(() => {
    return () => {
      utils.processamento.snapshot.invalidate();
      utils.processamento.historico.invalidate();
    };
  }, []);

  if (isLoading || !snap) {
    return (
      <div className="p-6 text-sm text-gray-500">Carregando métricas...</div>
    );
  }

  const chartData = (historico ?? []).map((s: any) => ({
    hora: new Date(s.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
    cpu: s.cpuPercent,
    ram: s.ramPercent,
    node: s.nodeRssMb,
  }));

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Processamento</h1>

      {/* Cards de métricas */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard title="CPU" value={`${snap.cpuPercent}%`} sub="uso atual" color={snap.cpuPercent > 80 ? "red" : snap.cpuPercent > 60 ? "yellow" : "green"} />
        <MetricCard
          title="RAM"
          value={`${snap.ramPercent}%`}
          sub={`${snap.ramUsedGb} GB / ${snap.ramTotalGb} GB`}
          color={snap.ramPercent > 85 ? "red" : snap.ramPercent > 70 ? "yellow" : "green"}
        />
        <MetricCard
          title="Node.js RSS"
          value={`${snap.nodeRssMb} MB`}
          sub={`Heap: ${snap.nodeHeapUsedMb}/${snap.nodeHeapTotalMb} MB`}
          color="blue"
        />
        <MetricCard
          title="Disco"
          value={`${snap.diskPercent}%`}
          sub={`${snap.diskUsedGb} GB / ${snap.diskTotalGb} GB`}
          color={snap.diskPercent > 85 ? "red" : snap.diskPercent > 70 ? "yellow" : "green"}
        />
        <MetricCard title="Uptime Servidor" value={formatUptime(snap.serverUptimeSeconds)} sub="desde o último boot" color="gray" />
        <MetricCard title="Uptime Aplicação" value={formatUptime(snap.processUptimeSeconds)} sub="desde o último restart" color="gray" />
      </div>

      {/* Info do sistema */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Informações do Sistema</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <InfoItem label="Node.js" value={snap.nodeVersion} />
          <InfoItem label="Plataforma" value={snap.platform} />
          <InfoItem label="Arquitetura" value={snap.arch} />
          <InfoItem label="CPUs" value={String(snap.cpuCount)} />
          <InfoItem label="PID" value={String(snap.pid)} />
        </div>
      </div>

      {/* Tabela de jobs */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Jobs em Execução</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 pr-4">Nome</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4">Intervalo</th>
                <th className="pb-2 pr-4">Última Execução</th>
                <th className="pb-2 pr-4">Duração</th>
                <th className="pb-2 pr-4">Média</th>
                <th className="pb-2 pr-4">Total Exec.</th>
                <th className="pb-2 pr-4">Erros</th>
                <th className="pb-2">Último Erro</th>
              </tr>
            </thead>
            <tbody>
              {(snap.jobs ?? []).map((job: any) => (
                <tr key={job.name} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-mono font-medium">{job.name}</td>
                  <td className="py-2 pr-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${job.running ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${job.running ? "bg-green-500" : "bg-gray-400"}`} />
                      {job.running ? "Rodando" : "Parado"}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-gray-600">{formatInterval(job.intervalMs)}</td>
                  <td className="py-2 pr-4 text-gray-600">{formatDate(job.lastRunAt)}</td>
                  <td className="py-2 pr-4 text-gray-600">{job.lastDurationMs != null ? `${job.lastDurationMs}ms` : "—"}</td>
                  <td className="py-2 pr-4 text-gray-600">{job.avgDurationMs != null ? `${job.avgDurationMs}ms` : "—"}</td>
                  <td className="py-2 pr-4 text-gray-600">{job.totalRuns}</td>
                  <td className="py-2 pr-4">
                    <span className={job.totalErrors > 0 ? "text-red-600 font-medium" : "text-gray-600"}>{job.totalErrors}</span>
                  </td>
                  <td className="py-2 text-gray-500 max-w-xs truncate" title={job.lastError ?? ""}>{job.lastError ?? "—"}</td>
                </tr>
              ))}
              {(snap.jobs ?? []).length === 0 && (
                <tr><td colSpan={9} className="py-4 text-center text-gray-400">Nenhum job registrado</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gráficos de performance */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Performance — Últimos 60 minutos</h2>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-400">Aguardando amostras (coletadas a cada 1 minuto)...</p>
        ) : (
          <div className="space-y-6">
            <ChartSection title="CPU (%)" dataKey="cpu" data={chartData} color="#f97316" domain={[0, 100]} />
            <ChartSection title="RAM (%)" dataKey="ram" data={chartData} color="#3b82f6" domain={[0, 100]} />
            <ChartSection title="Memória Node.js (MB)" dataKey="node" data={chartData} color="#8b5cf6" />
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ title, value, sub, color }: { title: string; value: string; sub: string; color: string }) {
  const colorMap: Record<string, string> = {
    red: "border-red-200 bg-red-50",
    yellow: "border-yellow-200 bg-yellow-50",
    green: "border-green-200 bg-green-50",
    blue: "border-blue-200 bg-blue-50",
    gray: "border-gray-200 bg-gray-50",
  };
  return (
    <div className={`border rounded-lg p-4 ${colorMap[color] ?? colorMap.gray}`}>
      <p className="text-xs text-gray-500 mb-1">{title}</p>
      <p className="text-xl font-bold text-gray-800">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{sub}</p>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="font-medium text-gray-700">{value}</p>
    </div>
  );
}

function ChartSection({ title, dataKey, data, color, domain }: { title: string; dataKey: string; data: any[]; color: string; domain?: [number, number] }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10 }} domain={domain ?? ["auto", "auto"]} width={36} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey={dataKey} stroke={color} dot={false} strokeWidth={1.5} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
